import { Switch, TableCell, TableRow, useTheme } from '@material-ui/core'
import React from 'react'
import {
    CellData,
    HeadCell,
    RenderRow,
    RowData,
    SimpleTable,
} from '../../components'
import { toMMDDYYYY } from '../../helpers'
import { Frequency, getAssignedUsersDisplay } from '../../models'
import { useStyles } from '../../styles'
import { PERIOD_OPTIONS } from './InspectionAssignModal'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    frequencyList: Frequency[]
    setFrequencyModalOpenViewOnly: (frequency: Frequency) => void
    toggleFrequencyActive: (id: number) => Promise<Frequency>
}

export const InspectionFrequencyTable = (props: Props) => {
    const { setFrequencyModalOpenViewOnly, toggleFrequencyActive } = props

    const theme = useTheme()
    const classes = useStyles()

    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.h6),
        fontWeight: 500,
    }

    interface FrequencyRowType extends RowData {
        name: CellData<string>
        inspectionType: CellData<string>
        period: CellData<string>
        unitCount: CellData<number>
        startPoint: CellData<string>
        assignedTo: CellData<string>
    }

    const headCells: HeadCell<FrequencyRowType>[] = [
        {
            disablePadding: false,
            align: 'left',
            id: 'name',
            label: 'Name',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'inspectionType',
            label: 'Inspection Type',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'period',
            label: 'Frequency',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'unitCount',
            label: 'Unit Count',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'startPoint',
            label: 'Initial Date',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'assignedTo',
            label: 'Assigned To',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'active',
            label: 'Active',
            style: headCellStyle,
        },
    ]

    const createRow = (freq: Frequency): FrequencyRowType => {
        const period =
            PERIOD_OPTIONS.find((period) => period.substring === freq.period)
                ?.name ?? ''

        // if (freq.period === 'DC') {
        //     period = freq.days + ' ' + period
        // }

        const start = new Date(freq.start_point)
        const startStr = toMMDDYYYY(start)
        const displayName = freq.name?.trim() || freq.inspection_type.name
        return {
            name: {
                value: displayName,
                sortValue: displayName,
            },
            inspectionType: {
                value: freq.inspection_type.name,
                sortValue: freq.inspection_type.name,
            },
            period: {
                value: period,
                sortValue: period,
            },
            unitCount: {
                value: freq.unit_ins?.length ?? 0,
                sortValue: freq.unit_ins?.length ?? 0,
            },
            startPoint: {
                value: startStr,
                sortValue: startStr,
            },
            assignedTo: {
                value: getAssignedUsersDisplay(
                    freq.assigned_users,
                    freq.assigned_to,
                ),
                sortValue: getAssignedUsersDisplay(
                    freq.assigned_users,
                    freq.assigned_to,
                ),
            },
            freq: { value: freq, sortValue: 1 },
        }
    }

    interface FrequencyRowProps {
        row: FrequencyRowType
    }

    const FreqRow: RenderRow<FrequencyRowProps> = (props) => {
        const { row } = props
        const cellStyle: React.CSSProperties = {
            ...typographyStyle(theme.typography.body2),
            fontWeight: 400,
        }

        return (
            <TableRow
                key={`FREQ_ROW_${row.freq.value.id}`}
                style={{
                    cursor: 'pointer',
                }}
                onClick={() => {
                    setFrequencyModalOpenViewOnly(row.freq.value)
                }}
                className={classes.hoverGrey300}
            >
                <TableCell style={cellStyle}>{row.name.value}</TableCell>
                <TableCell style={cellStyle}>
                    {row.inspectionType.value}
                </TableCell>
                <TableCell align="center" style={cellStyle}>
                    {row.period.value}
                </TableCell>
                <TableCell align="center" style={cellStyle}>
                    {row.unitCount.value}
                </TableCell>
                <TableCell align="center" style={cellStyle}>
                    {row.startPoint.value}
                </TableCell>
                <TableCell style={cellStyle}>{row.assignedTo.value}</TableCell>
                <TableCell align="center" style={cellStyle}>
                    <Switch
                        color="primary"
                        checked={row.freq.value.active}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() =>
                            toggleFrequencyActive(row.freq.value.id)
                        }
                    />
                </TableCell>
            </TableRow>
        )
    }

    return (
        <SimpleTable<FrequencyRowType>
            rows={props.frequencyList?.map((freq) => createRow(freq)) ?? []}
            render={(row) => <FreqRow row={row} />}
            orderByDefault={'name'}
            headCells={headCells}
        />
    )
}
